import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import warning from "../../resources/icons/red-warning.svg";
import BlueWarning from "../../resources/icons/blue-warning.svg";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function TenantApprovalModal({
  isModalOpen,
  toggleModal,
  actionType,
  handleConfirmAction,
  issuerComments,
  setIssuerComments,
  isLegacy = false,
  matchingVCs = [],
  vcStatus,
  testId = "verifier-approval-modal",
}) {
  const { t } = useTranslation();
  const commentsInputId = `${testId}-comments`;

  function closeModal() {
    toggleModal(false);
  }

  if (!actionType) {
    return null;
  }

  const getConfirmationMessage = () => {
    if (actionType === "Approved" && isLegacy && matchingVCs.length === 0) {
      return `${t("verifierApproval:no_matching_credentials_found_approve")}`;
    } else if (actionType === "Approved") {
      return `${t("verifierApproval:are_you_sure_approve_request")}`;
    } else {
      return `${t("verifierApproval:are_you_sure_reject_request")}`;
    }
  };

  const isConfirmDisabled = () => {
    return (
      (actionType === "Rejected" || actionType === "Approved") &&
      (!issuerComments || !issuerComments.trim())
    );
  };

  return (
    <>
      <Transition appear show={isModalOpen ? true : false} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              id={`${testId}-backdrop`}
              className="fixed inset-0 bg-black/25 backdrop-blur-md"
            />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  id={testId}
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative"
                >
                  <button
                    type="button"
                    id={`${testId}-close`}
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>
                  <Dialog.Title
                    as="h3"
                    className="text-lg flex justify-center font-medium leading-6 text-gray-900"
                  >
                    <img
                      src={
                        actionType === "Approved"
                          ? BlueWarning
                          : actionType === "Rejected"
                          ? warning
                          : warning
                      }
                      alt="warning"
                    />
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-lg font-medium flex justify-center">
                      {actionType === "Approved"
                        ? t("verifierApproval:approve")
                        : t("verifierApproval:reject")}
                    </p>
                    {actionType !== "Approved" && (
                      <p className="text-sm text-gray-500 mt-2 flex justify-center">
                        {getConfirmationMessage()}
                      </p>
                    )}
                    {vcStatus === "Revoked" && actionType === "Approved" && (
                      <div className="p-3 bg-yellow-50 border-yellow-200 rounded-lg my-3">
                        <div className="flex items-center">
                          <div className="mr-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                            <AlertTriangle className="h-5 w-5" />
                          </div>
                          <p className="text-sm font-medium text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                            {t("verifierApproval:credential_revoked_approve")}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mt-3">
                      <label
                        htmlFor={commentsInputId}
                        className="block text-sm font-medium text-gray-700 mb-0.5"
                      >
                        {actionType === "Approved"
                          ? t("verifierApproval:please_add_comment_approve")
                          : t(
                              "verifierApproval:please_provide_reason_rejection"
                            )}
                      </label>
                      <textarea
                        id={commentsInputId}
                        value={issuerComments}
                        onChange={(e) => setIssuerComments(e.target.value)}
                        className={`w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${
                          actionType === "Approved"
                            ? "focus:ring-blue-500"
                            : "focus:ring-red-500"
                        }`}
                        rows={3}
                        placeholder={
                          actionType === "Approved"
                            ? t("verifierApproval:enter_approval_comment")
                            : t("verifierApproval:enter_rejection_reason")
                        }
                        style={{ fontSize: "0.875rem" }}
                        required={
                          actionType === "Rejected" || actionType === "Approved"
                        }
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-center gap-4">
                    <button
                      id={`${testId}-cancel`}
                      data-modal-hide="popup-modal"
                      type="button"
                      onClick={closeModal}
                      className="py-2.5 px-5 ms-3 text-sm hover:bg-gray-200 font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100"
                    >
                      {t("verifierApproval:cancel")}
                    </button>
                    <button
                      id={`${testId}-confirm`}
                      data-modal-hide="popup-modal"
                      type="button"
                      onClick={handleConfirmAction}
                      className={`text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center transition-colors ${
                        isConfirmDisabled()
                          ? actionType === "Approved"
                            ? "bg-blue-300 cursor-not-allowed opacity-70"
                            : "bg-red-300 cursor-not-allowed opacity-70"
                          : actionType === "Approved"
                          ? "bg-cfPrimaryButton hover:bg-cfPrimaryDark"
                          : "bg-[#D94D40] hover:bg-[#b84035]"
                      }`}
                      disabled={isConfirmDisabled()}
                    >
                      {actionType === "Approved"
                        ? t("verifierApproval:approve")
                        : t("verifierApproval:reject")}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
